import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Plus, Edit2, Trash2, Shirt, QrCode, Printer, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Wardrobes = () => {
  const { getAuthHeaders, isManager } = useAuth();
  const navigate = useNavigate();
  const [wardrobes, setWardrobes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [formData, setFormData] = useState({ name: '', label: '', description: '', location: '' });

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/wardrobes`, { headers: getAuthHeaders() });
      setWardrobes(response.data);
    } catch (error) {
      toast.error('Failed to fetch wardrobes');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenDialog = (w = null) => {
    if (w) {
      setSelected(w);
      setFormData({ name: w.name, label: w.label, description: w.description || '', location: w.location || '' });
    } else {
      setSelected(null);
      setFormData({ name: '', label: '', description: '', location: '' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (selected) {
        await axios.put(`${API_URL}/api/wardrobes/${selected.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Wardrobe updated');
      } else {
        await axios.post(`${API_URL}/api/wardrobes`, formData, { headers: getAuthHeaders() });
        toast.success('Wardrobe created');
      }
      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/wardrobes/${selected.id}`, { headers: getAuthHeaders() });
      toast.success('Wardrobe deleted');
      setShowDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const printLabels = (list) => {
    if (list.length === 0) return;
    const printWindow = window.open('', '_blank');
    const labels = list.map(w => {
      const qrUrl = `${window.location.origin}/wardrobe/${w.id}`;
      return `
        <div class="qr-container">
          <div class="label">${w.label}</div>
          <div class="name">${w.name}</div>
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(qrUrl)}" />
          <div style="font-size: 10px; color: #888;">Scan for contents</div>
        </div>
      `;
    }).join('');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Wardrobe Labels</title>
          <style>
            body { font-family: Arial; padding: 10px; }
            .grid { display: flex; flex-wrap: wrap; gap: 10px; }
            .qr-container { display: inline-block; padding: 15px; border: 1px solid #000; text-align: center; width: 200px; page-break-inside: avoid; }
            .label { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
            .name { font-size: 12px; color: #666; margin-bottom: 10px; }
            @media print { body { padding: 0; margin: 0; } }
          </style>
        </head>
        <body>
          <div class="grid">${labels}</div>
          <script>window.onload = function() { setTimeout(function() { window.print(); }, 500); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === wardrobes.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(wardrobes.map(w => w.id)));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="wardrobes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Wardrobes</h1>
          <p className="text-muted-foreground mt-1">Manage wardrobes and racks for costumes</p>
        </div>
        <div className="flex gap-2">
          {selectedIds.size > 0 && (
            <Button variant="outline" onClick={() => printLabels(wardrobes.filter(w => selectedIds.has(w.id)))}>
              <Printer className="w-4 h-4 mr-2" />
              Print Labels ({selectedIds.size})
            </Button>
          )}
          {isManager() && (
            <Button data-testid="add-wardrobe-btn" onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Wardrobe
            </Button>
          )}
        </div>
      </div>

      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-center gap-2">
          <Shirt className="w-5 h-5 text-muted-foreground" />
          <CardTitle className="text-lg">All Wardrobes ({wardrobes.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={wardrobes.length > 0 && selectedIds.size === wardrobes.length} onCheckedChange={toggleSelectAll} />
                </TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-center">Costumes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {wardrobes.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No wardrobes yet. Add your first wardrobe.</TableCell></TableRow>
              ) : (
                wardrobes.map((w) => (
                  <TableRow key={w.id} className="table-row-hover">
                    <TableCell>
                      <Checkbox checked={selectedIds.has(w.id)} onCheckedChange={() => toggleSelect(w.id)} />
                    </TableCell>
                    <TableCell><code className="text-sm bg-muted px-2 py-1 rounded font-mono font-bold">{w.label}</code></TableCell>
                    <TableCell className="font-medium">{w.name}</TableCell>
                    <TableCell>{w.location || '-'}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">{w.description || '-'}</TableCell>
                    <TableCell className="text-center">
                      <span className="inline-block bg-primary/10 text-primary px-2 py-1 rounded text-xs font-semibold">
                        {w.costume_count}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => navigate(`/wardrobe/${w.id}`)} title="View Contents">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => { setSelected(w); setShowQRDialog(true); }} title="QR Code">
                          <QrCode className="w-4 h-4" />
                        </Button>
                        {isManager() && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(w)}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => { setSelected(w); setShowDeleteDialog(true); }} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{selected ? 'Edit Wardrobe' : 'Add Wardrobe'}</DialogTitle>
            <DialogDescription>Create wardrobes/racks for organizing costumes</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Label * (e.g., WARD-01)</Label>
              <Input value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value.toUpperCase() })} placeholder="WARD-01" className="font-mono" />
            </div>
            <div className="grid gap-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Victorian Costumes Rack" />
            </div>
            <div className="grid gap-2">
              <Label>Location</Label>
              <Input value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} placeholder="Costume Room A" />
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!formData.name || !formData.label}>{selected ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Wardrobe QR Code</DialogTitle>
            <DialogDescription>Scan to see contents</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="flex flex-col items-center py-6 space-y-4">
              <div className="bg-white p-4 rounded-xl">
                <QRCodeSVG value={`${window.location.origin}/wardrobe/${selected.id}`} size={200} level="H" />
              </div>
              <div className="text-center">
                <p className="font-bold text-xl">{selected.label}</p>
                <p className="text-sm text-muted-foreground">{selected.name}</p>
              </div>
              <Button onClick={() => printLabels([selected])} className="w-full">
                <Printer className="w-4 h-4 mr-2" />
                Print Label
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Wardrobe</DialogTitle>
            <DialogDescription>Delete "{selected?.name}"? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Wardrobes;
